"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

interface TooltipsProps {
  placement?: 'top' | 'right' | 'bottom' | 'left' | 'bottom-right'
  style?: 'light' | 'dark' | 'warning'
  message?: string
  className?: string
  children?: React.ReactNode
}

export default function Tooltips({ placement = 'bottom', style = 'dark', message, children, className }: TooltipsProps) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const transform = useMemo(() => {
    switch (placement) {
      case 'top':
        return 'translate(-50%, -100%)'
      case 'right':
        return 'translate(0, -50%)'
      case 'left':
        return 'translate(-100%, -50%)'
      case 'bottom-right':
        return 'translate(-100%, 0)'
      default:
        return 'translate(-50%, 0)'
    }
  }, [placement])

  const updatePosition = useCallback(() => {
    const trigger = anchorRef.current?.parentElement
    if (!trigger) {
      return
    }

    const offset = 12
    const rect = trigger.getBoundingClientRect()

    if (placement === 'top') {
      setPosition({ left: rect.left + rect.width / 2, top: rect.top - offset })
      return
    }

    if (placement === 'right') {
      setPosition({ left: rect.right + offset, top: rect.top + rect.height / 2 })
      return
    }

    if (placement === 'left') {
      setPosition({ left: rect.left - offset, top: rect.top + rect.height / 2 })
      return
    }

    if (placement === 'bottom-right') {
      setPosition({ left: rect.right, top: rect.bottom + offset })
      return
    }

    setPosition({ left: rect.left + rect.width / 2, top: rect.bottom + offset })
  }, [placement])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const trigger = anchorRef.current?.parentElement
    if (!trigger) {
      return
    }

    const open = () => {
      setVisible(true)
      updatePosition()
    }
    const close = () => setVisible(false)

    trigger.addEventListener('mouseenter', open)
    trigger.addEventListener('mouseleave', close)
    trigger.addEventListener('focusin', open)
    trigger.addEventListener('focusout', close)

    return () => {
      trigger.removeEventListener('mouseenter', open)
      trigger.removeEventListener('mouseleave', close)
      trigger.removeEventListener('focusin', open)
      trigger.removeEventListener('focusout', close)
    }
  }, [updatePosition])

  useEffect(() => {
    if (!visible) {
      return
    }

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [visible, updatePosition])

  return (
    <>
      <span ref={anchorRef} className="sr-only" aria-hidden="true" />
      {mounted && visible &&
        createPortal(
          <div
            className={cn(
              'pointer-events-none fixed w-max px-2 py-1 rounded text-sm animate-fadeIn z-10',
              {
                'bg-[rgba(66,66,66,.88)] text-white': Boolean(style === 'dark'),
                'border opacity-100 border-[rgba(66,66,66,.88)] bg-white text-[rgba(66,66,66,.88)] font-semibold': Boolean(style === 'light'),
                'border opacity-100 border-[rgba(0,0,0,.2)] bg-[#fff2c4] text-[rgba(66,66,66,.88)] font-semibold': Boolean(style === 'warning'),
              },
              className,
            )}
            style={{ left: position.left, top: position.top, transform }}
          >
            {placement === 'bottom-right' && (
              <div
                className={cn('absolute bottom-full right-4 arrow-top', {
                  'border-b-[rgba(0,0,0,.2)] after:border-b-[#fff2c4]': style === 'warning',
                  'border-b-[rgba(0,0,0,.2)] after:border-b-white': style === 'light',
                })}
              ></div>
            )}
            {placement === 'right' && (
              <div
                className={cn('absolute top-2/4 -translate-y-2/4 right-full arrow-left', {
                  'border-r-[rgba(0,0,0,.2)] after:border-r-[#fff2c4]': style === 'warning',
                  'border-r-[rgba(0,0,0,.2)] after:border-r-white': style === 'light',
                })}
              ></div>
            )}
            <span>{message}</span>
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}
